import {
  Button,
  Div,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  noItems,
  uploadBrandTemplateMenuImportPreviewImage,
  reportError,
} from '@vgrubs/components';
import {
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
  BTModifierGroupItemResponse,
} from 'components/src/types/brand.types';
import { MenuItemV2Response } from 'components/src/types/menu.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import { AddNewModifierGroupModal } from '../Modifiers/AddNewModifierGroupModal';
import { AddItemModal } from './AddItemModal';
import { AddModifierGroupModal } from './AddModifierGroupModal';
import { DeleteItemModal } from './DeleteItemModal';
import { MenuItemTypeComponent } from './MenuItemComponents';
import { PausedItemModal } from './PausedItemModal';

export const Items = ({
  menu,
  setMenu,
  setLoading,
}: {
  menu: BTMenuResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<{
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  } | null>(null);
  const [pausedModal, setPausedModal] = useState(false);
  const [addModifierGroupModal, setAddModifierGroupModal] = useState(false);
  const [modifierGroups, setModifierGroups] = useState<
    BTModifierGroupItemResponse[]
  >([]);
  const [deleteModal, setDeleteModal] = useState<{
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  } | null>(null);
  const [addNewModifierGroupModal, setAddNewModifierGroupModal] =
    useState(false);

  const { brandTemplateId } = useParams() as {
    brandTemplateId: string;
  };

  async function handleAddItem(
    image: BTMenuItemResponse['imageUrl'] | File | Blob | null,
    name: MenuItemV2Response['name'],
    description: BTMenuItemResponse['description'],
    fromPrice: BTMenuItemResponse['fromPrice'],
    toPrice: BTMenuItemResponse['toPrice'],
    categoryId: BTCategoryResponse['id'],
    modifierGroups: BTMenuItemResponse['modifierGroups'],
    emptyForm: () => void
  ) {
    const uuid = Math.floor(Math.random() * 1000000);

    const body = {
      imageUrl: image as string,
      name,
      description,
      fromPrice,
      toPrice,
      modifierGroups: modifierGroups,
    };

    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.categories.map((category) => {
        if (category.id === categoryId) {
          return { ...category, items: [...category.items, body] };
        } else return category;
      });

      return { ...prev, categories: updated } as BTMenuResponse;
    });

    setAddModal(false);
    emptyForm();
  }

  async function handleEditItem(
    image: BTMenuItemResponse['imageUrl'] | File | Blob | null,
    name: BTMenuItemResponse['name'],
    description: BTMenuItemResponse['description'],
    fromPrice: BTMenuItemResponse['fromPrice'],
    toPrice: BTMenuItemResponse['toPrice'],
    categoryId: BTCategoryResponse['id'],
    modifierGroups: BTMenuItemResponse['modifierGroups'],
    existingItem: BTMenuItemResponse,
    existingCategoryId: BTCategoryResponse['id'],
    emptyForm: () => void
  ) {
    const body = {
      imageUrl: image as string,
      name,
      description,
      fromPrice,
      toPrice,
      categoryId,
      modifierGroups: modifierGroups,
    };
    // Update the item and move it if categoryId has changed
    setMenu((prev) => {
      if (!prev) return prev;

      // If the category ID has changed, move the item to the new category
      if (existingCategoryId !== categoryId) {
        // 1. Remove the item from the old category
        const updated = prev.categories.map((category) => {
          if (category.id === existingCategoryId) {
            return {
              ...category,
              items: category.items.filter(
                (item) => item.id !== existingItem.id
              ),
            };
          }
          return category;
        });

        // 2. Add the item to the new category
        const newUpdated = updated.map((category) => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: [...category.items, { ...existingItem, ...body }], // Add the edited item
            };
          }
          return category;
        });

        return { ...prev, categories: newUpdated } as BTMenuResponse; // Return updated categories with moved item
      } else {
        // If the category ID hasn't changed, just update the item in the current category
        const updated = prev.categories.map((category) => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map((item) =>
                item.id === existingItem.id ? { ...item, ...body } : item
              ),
            };
          }
          return category;
        });

        return { ...prev, categories: updated } as BTMenuResponse; // Return updated categories
      }
    });

    emptyForm();
    setEditModal(null);
  }

  async function handleDeleteItem() {
    if (!deleteModal) return;
    const deletedItemId = deleteModal.item.id;
    const categoryId = deleteModal.category.id;

    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.categories.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            items: category.items.filter((item) => item.id !== deletedItemId),
          };
        }
        return category;
      });

      return { ...prev, categories: updated };
    });

    setDeleteModal(null);
  }

  async function handleUploadImage(image: File | Blob | string | null) {
    if (!image) return;
    const formData = new FormData();
    formData.append('image', image);

    try {
      setLoading(true);
      const { data } = await uploadBrandTemplateMenuImportPreviewImage(
        brandTemplateId,
        formData
      );

      return data.data;
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const AddItemButton = () => {
    return (
      <Button $wSM="200px" $selected onClick={() => setAddModal(true)}>
        Add Item
      </Button>
    );
  };

  const totalChildrenCount = menu?.categories?.reduce((count, category) => {
    return count + (category.items ? category.items.length : 0);
  }, 0);

  return (
    <Div>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">Items {menu?.categories && `(${totalChildrenCount})`}</H3>
        {menu?.categories && menu?.categories?.length > 0 && (
          <Icon $pointer onClick={() => setAddModal(true)}>
            <PlusIcon />
          </Icon>
        )}
      </Div>
      <div>
        {menu?.categories?.map((item, i) => (
          <MenuItemTypeComponent
            key={i}
            itemType={item}
            onClick={setEditModal}
            onClickDelete={(id) => setDeleteModal(id)}
            menu={menu}
            setMenu={setMenu}
          />
        ))}
      </div>

      {menu?.categories && menu?.categories?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noItems} $mb="24" />
          <H3 $mb="24" $center>
            No Items
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu items.
          </P>
          <AddItemButton />
        </Div>
      )}

      <AddItemModal
        menu={menu}
        addModal={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleAddItem={handleAddItem}
        handleUploadImage={handleUploadImage}
      />

      <AddItemModal
        menu={menu}
        addModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        setAddModifierGroupModal={setAddModifierGroupModal}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        handleEditItem={handleEditItem}
        edit
        handleUploadImage={handleUploadImage}
      />

      <PausedItemModal
        pausedModal={pausedModal}
        closeModal={() => setPausedModal(false)}
      />
      <AddModifierGroupModal
        addModifierGroupModal={addModifierGroupModal}
        menu={menu}
        setModifierGroups={setModifierGroups}
        modifierGroups={modifierGroups}
        setAddNewModifierGroupModal={setAddNewModifierGroupModal}
        closeModal={() => setAddModifierGroupModal(false)}
      />

      <DeleteItemModal
        deleteModal={deleteModal}
        closeModal={() => setDeleteModal(null)}
        handleDeleteItem={handleDeleteItem}
      />

      <AddNewModifierGroupModal
        addNewModal={addNewModifierGroupModal}
        setMenu={setMenu}
        closeModal={() => setAddNewModifierGroupModal(false)}
      />
    </Div>
  );
};
